import { useEffect, useRef, useState } from "react";
import { ICustomerAddress } from "../models/ICustomerAddress";
import { getCustomerAddressList, deleteCustomerAddress } from "../services/customerAddress";
import { getUserToken, saveAddresses } from "../utils/tokenUtils";
import { useLanguage } from "../context/LanguageContext";
import AdresimPopup from "../popupPages/AdresimPopup";
import axios from "axios";
import "../styles/style.css";

function AddressCard({
  address,
  isDefault,
  onClick,
  onDelete,
}: {
  address: ICustomerAddress;
  isDefault: boolean;
  onClick: () => void;
  onDelete: (e: React.MouseEvent) => void;
}) {
  return (
    <div
      className={`address-card${isDefault ? " address-card--default" : ""}`}
      onClick={onClick}
      style={{ position: "relative" }}
    >
      <button
        className="btn btn-sm btn-danger rounded-circle"
        style={{ position: "absolute", top: "8px", left: "8px", width: "26px", height: "26px", padding: 0, lineHeight: 1, zIndex: 1 }}
        onClick={onDelete}
        title="Sil"
      >
        <i className="bi bi-x" style={{ fontSize: "1rem" }}></i>
      </button>
      <div className="address-card__icon">
        <i className={`bi ${isDefault ? "bi-map-fill" : "bi-geo-alt-fill"}`}></i>
      </div>
      <div className="address-card__city">
        {address.city.cityNameTr} / {address.town.townNameTr}
      </div>
      {address.customerAddressDescription && (
        <div className="address-card__detail">{address.customerAddressDescription}</div>
      )}
      {address.customerAddressNote && (
        <div className="address-card__note">{address.customerAddressNote}</div>
      )}
    </div>
  );
}

function Adreslerim() {
  const [addresses, setAddresses] = useState<ICustomerAddress[]>([]);
  const [loading, setLoading] = useState(true);
  const [editAddress, setEditAddress] = useState<ICustomerAddress | null>(null);
  const [openKey, setOpenKey] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState<ICustomerAddress | null>(null);
  const [deleting, setDeleting] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { t } = useLanguage();

  const fetchAddresses = () => {
    const token = getUserToken();
    if (token) {
      getCustomerAddressList(token.userId)
        .then((res) => {
          setAddresses(res.data);
          saveAddresses(res.data);
        })
        .catch((err) => {
          if (axios.isAxiosError(err) && err.response?.status === 404) {
            setAddresses([]);
          }
          // 401 → interceptor zaten logout + login popup açıyor, burada sessizce geç
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAddresses();
  }, []);

  useEffect(() => {
    if (openKey > 0) {
      triggerRef.current?.click();
    }
  }, [openKey]);

  const openAdd = () => {
    setEditAddress(null);
    setOpenKey((k) => k + 1);
  };

  const openEdit = (addr: ICustomerAddress) => {
    setEditAddress(addr);
    setOpenKey((k) => k + 1);
  };

  const handleSuccess = () => {
    fetchAddresses();
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    const token = getUserToken();
    if (!token) return;
    setDeleting(true);
    try {
      await deleteCustomerAddress(token.userId, deleteTarget.id);
      setDeleteTarget(null);
      fetchAddresses();
    } catch {
      // hata sessizce geçilir, adresler yenilenecek
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="container py-4">
        <div className="skeleton mb-4" style={{ height: "13px", width: "25%" }} />
        <div className="row justify-content-center mb-5">
          <div className="col-12 col-sm-8 col-md-5">
            <div className="skeleton" style={{ height: "110px", borderRadius: "16px" }} />
          </div>
        </div>
        <div className="skeleton mb-4" style={{ height: "13px", width: "35%" }} />
        <div className="row g-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="col-12 col-md-4">
              <div className="skeleton" style={{ height: "90px", borderRadius: "16px" }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const defaultAddress = addresses.find((a) => a.customerAddressIsDefault);
  const otherAddresses = addresses.filter((a) => !a.customerAddressIsDefault);

  return (
    <div className="container py-4">
      {addresses.length === 0 ? (
        <div className="text-center text-muted mt-5">
          <i className="bi bi-geo-alt" style={{ fontSize: "3rem" }}></i>
          <p className="mt-2">{t("addresses.noAddresses")}</p>
        </div>
      ) : (
        <>
          {defaultAddress && (
            <section className="mb-5">
              <h6 className="address-section-title">{t("addresses.myAddress")}</h6>
              <div className="row justify-content-center">
                <div className="col-12 col-sm-8 col-md-5">
                  <AddressCard
                    address={defaultAddress}
                    isDefault={true}
                    onClick={() => openEdit(defaultAddress)}
                    onDelete={(e) => { e.stopPropagation(); setDeleteTarget(defaultAddress); }}
                  />
                </div>
              </div>
            </section>
          )}

          {otherAddresses.length > 0 && (
            <section className="mb-4">
              <h6 className="address-section-title">{t("addresses.otherAddresses")}</h6>
              <div className="row g-3">
                {otherAddresses.map((addr) => (
                  <div key={addr.id} className="col-12 col-md-4">
                    <AddressCard
                      address={addr}
                      isDefault={false}
                      onClick={() => openEdit(addr)}
                      onDelete={(e) => { e.stopPropagation(); setDeleteTarget(addr); }}
                    />
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {addresses.length >= 5 && (
        <div className="alert alert-warning small text-center mt-4 mb-0 py-2">
          <i className="bi bi-exclamation-triangle-fill me-1"></i>
          {t("addresses.maxAddresses")}
        </div>
      )}
      <div className="d-grid mt-2">
        <button
          className="btn btn-danger rounded-pill py-2 fw-semibold"
          onClick={openAdd}
          disabled={addresses.length >= 5}
        >
          <i className="bi bi-plus-circle me-2"></i>{t("addresses.addAddress")}
        </button>
      </div>

      <button
        ref={triggerRef}
        data-bs-toggle="modal"
        data-bs-target="#adresimModal"
        className="d-none"
      />

      <AdresimPopup address={editAddress} onSuccess={handleSuccess} />

      {/* Silme onay popup */}
      {deleteTarget && (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content rounded-4 shadow">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-trash3 text-danger me-2"></i>Adresi Sil
                </h5>
              </div>
              <div className="modal-body">
                <p className="mb-1">Bu adresi silmek istediğinizden emin misiniz?</p>
                <p className="fw-semibold text-muted small mb-0">
                  {deleteTarget.city.cityNameTr} / {deleteTarget.town.townNameTr}
                  {deleteTarget.customerAddressDescription ? ` — ${deleteTarget.customerAddressDescription}` : ""}
                </p>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => setDeleteTarget(null)}
                  disabled={deleting}
                >
                  İptal
                </button>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={handleDeleteConfirm}
                  disabled={deleting}
                >
                  {deleting ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-trash3 me-1"></i>}
                  Evet, Sil
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Adreslerim;
